import React, { useEffect, useState, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { M_StateActions } from "../M_Store/stateSlice";
import "./M_Table.css";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { DataTable } from "primereact/datatable/datatable.cjs.js";
import { Column } from "primereact/column/column.cjs.js";
import { ContextMenu } from "primereact/contextmenu/contextmenu.cjs.js";
import { Paginator } from "primereact/paginator/paginator.cjs.js";
import {
  CrudTable,
  BufferService,
  FiltersSortService,
  LoadingBar,
  LoadingContext,
  MButton,
  MMessageBoard,
  NavigateContext,
  MCheckBox,
} from "../../M_Components";
import PropTypes from "prop-types";
import { TableMessageBoards } from "./TableMessageBoards";
import {
  loadingStylesFunc,
  sortFields,
  crudInPopUp,
  columnOrderHeaderIcon,
  auditActionColor,
  sortAuditData,
  columnInfoHeaderIcon,
  getColumnObj,
  sessionStorageMachine,
  selectedIncomingValue,
  deRetardationString,
  getType,
  getValue,
  getColumnName,
  loadingStyle,
  getSeverity,
} from "./TableServices";
import { TableHeader } from "./TableHeader";

export function MTable({
  entityName,
  useSessionStorage,
  crud,
  onPassLine,
  onPassContext,
  tableHeight,
  manualId,
  rolePermissions,
  lineContext,
  extraButtons,
  removeContextItems,
  onHideLabels,
  toUseFilter,
  toUseFilterName,
  selectSingle,
  refresh,
  setRefresh,
  isParentTable,
  isChildTable,
  setParentLine,
  parentLine,
  field,
  roleName,
  FILTER,
  replaceFunctions,
  color,
  secondColor,
  rowGroup,
  groupField,
  headerTemplate,
  footerTemplate,
  colorOptions,
  noSelect,
  maxRows,
  includeImportExcel,
  includeTemplateExcel,
  value,
  valueIdField,
  removeHeaders,
  filtersOnTable,
  infoElement,
  orderFields,
  hideDefaultColor,
  inputFilter,
  stopSaveRows,
  removeColumns,
  removeFilters,
  disable,
  tabsRailWidth,
  onDataLoaded,
  // Drives automatic selection of the first row in single-selection mode
  autoSelectFirstRow,
}) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  const ctx = useContext(LoadingContext);
  const ctx2 = useContext(NavigateContext);

  // Resolve main colors (fallback to context colors)
  function passColor() {
    return color || ctx.primaryColor || "var(--color-black)";
  }
  color = passColor();

  function passSecondColor() {
    return secondColor || ctx.secondaryColor || "var(--color-black)";
  }
  secondColor = passSecondColor();

  removeHeaders = removeHeaders || [];

  // Resolve name used to match role permissions
  function passNameForRole() {
    return roleName || entityName;
  }
  const nameForRole = passNameForRole();

  // Single vs multiple selection
  function passSelectedSingle() {
    return selectSingle !== undefined ? selectSingle : false;
  }
  const isSelectSingle = passSelectedSingle();

  // Saved views: default name
  const passFilterName = toUseFilterName || "Default";

  // Initial view filters passed in props (Map of viewName -> query string)
  function passedFilter() {
    return toUseFilter || new Map();
  }
  const passFilter = passedFilter();

  const removeItem = removeContextItems || [];
  const newButtons = extraButtons || [];
  const roles = rolePermissions || [];
  const contextMenu = lineContext || [];
  const entity = entityName;

  // Enable/disable session storage persistence
  function passIsPage() {
    return useSessionStorage !== undefined ? useSessionStorage : true;
  }
  const isPage = passIsPage();

  // Enable/disable CRUD behavior
  function passIsCrud() {
    return crud !== undefined ? crud : true;
  }
  const isCrud = passIsCrud();

  // Media queries to adapt layout and paginator
  const adjustingTableMedia2 = useMediaQuery("(min-width:1200px)");
  const adjustingTableMedia3 = useMediaQuery("(min-width:500px)");
  const match = useMediaQuery("(max-width:920px)");
  const match2 = useMediaQuery("(max-width:820px)");

  const { t, i18n } = useTranslation();
  const initialLanguage = useRef(i18n.language);

  const maxWidth = useMediaQuery("(min-width:1050px)");

  // Tab/session id
  const [id, setId] = useState(
    manualId === undefined
      ? M_State.filter((obj) => obj.key === "selectedTab")[0]?.value
      : manualId
  );

  // Table rows and columns
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);

  const [companies, setCompanies] = useState([]);

  // Pagination state
  const [firstRows, setFirstRows] = useState(0);
  const [page, setPage] = useState(1);
  function passPageSize() {
    return maxRows || 20;
  }
  const [pageSize, setPageSize] = useState(passPageSize());
  const [totalRows, setTotalRows] = useState(50);

  // Which columns are visible
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Selected table rows
  function passSelectedRows() {
    return (Array.isArray(value) && value && [...value]) || value || [];
  }
  const [selectedRows, setSelectedRows] = useState(passSelectedRows());

  // Context menu selected row
  const [selectContext, setSelectContext] = useState(null);

  // Filtering and sorting state
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [orderInfo, setOrderInfo] = useState({
    columnName: "",
    orderbool: false,
  });
  const [metaPair, setMetaPair] = useState([]); // [{name,type,operation,value}]
  const [metaType, setMetaType] = useState([]); // column metadata
  const [advFilterString, setAdvFilterString] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [expandedRows, setExpandedRows] = useState([]);

  // CRUD / modal state
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [valueAdd, setValueAdd] = useState(new Map());
  const [rowEdit, setRowEdit] = useState([]);
  const [editMap, setEditMap] = useState(new Map());
  const [row, setRow] = useState(null);
  const [operation, setOperation] = useState("");

  // Column visibility modal
  const [filteredColumnModal, setFilteredColumnModal] = useState([]);
  const [valueFilteredColumnModal, setValueFilteredColumnModal] = useState("");
  const [columnContextMenuString, setColumnContextMenuString] = useState({});

  // Saved views (viewName -> query string)
  const [views, setViews] = useState(passFilter);
  const [selectedView, setSelectedView] = useState(passFilterName);

  // Lifecycle flags
  const [isFirstLoop, setIsFirstLoop] = useState(true);
  const [isTable, setIsTable] = useState(true);
  const [isLoaded, setIsLoaded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);

  // -------- Robust viewport-based table height calculation --------
  const computeTableHeight = React.useCallback(() => {
    // If the caller forces a height, just use it.
    if (tableHeight) return tableHeight;

    // Use viewport height instead of document.body.offsetHeight
    let viewportHeight = 800;
    if (typeof window !== "undefined" && window.innerHeight) {
      viewportHeight = window.innerHeight;
    } else if (document.documentElement?.clientHeight) {
      viewportHeight = document.documentElement.clientHeight;
    }

    const headerSize =
      document.body.getElementsByClassName("p-datatable-header")[0]
        ?.offsetHeight || 60;
    const paginatorSize =
      document.body.getElementsByClassName("paginator")[0]?.offsetHeight || 60;
    const tabSize =
      document.body.getElementsByClassName("divTabs")[0]?.offsetHeight || 50;

    const available =
      viewportHeight - (headerSize + paginatorSize + tabSize + 3);

    // Guard rails: don't let the table collapse too much on tiny screens
    return available > 200 ? available : 200;
  }, [tableHeight]);

  const [height, setHeight] = useState(() => computeTableHeight());

  useEffect(() => {
    // Recalculate height on initial mount and on window resize
    const handleResize = () => {
      setHeight(computeTableHeight());
    };

    handleResize();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [computeTableHeight]);
  // ----------------------------------------------------------------

  // BufferService instances
  const bufferService = new BufferService(entity);
  const filterService = new FiltersSortService();
  const dt = useRef(null);
  const cm = useRef(null);
  const ccm = useRef(null);

  // Audit service
  const auditService = new BufferService("audit");
  const [openAudit, setOpenAudit] = useState(false);
  const [auditData, setAuditData] = useState([]);

  // Context menu row for delete/copy/edit
  const [dataContextMenu, setDataContextMenu] = useState(null);

  // Column context menu items (sort/remove)
  const columnContextMenu = [
    {
      label: t("OrderAsc"),
      icon: (
        <i className="pi pi-sort-amount-down-alt" style={{ color: color }}></i>
      ),
      command: () => {
        const info = {
          columnName: columnContextMenuString.target.title,
          orderbool: false,
        };
        setOrderInfo(info);
        getWithFilters(info);
      },
    },
    {
      label: t("OrderDesc"),
      icon: (
        <i className="pi pi-sort-amount-up-alt" style={{ color: color }}></i>
      ),
      command: () => {
        const info = {
          columnName: columnContextMenuString.target.title,
          orderbool: true,
        };
        setOrderInfo(info);
        getWithFilters(info);
      },
    },
    {
      label: t("Remove"),
      icon: <i className="pi pi-times" style={{ color: color }}></i>,
      command: () =>
        onColumnToggle(getColumnObj(columnContextMenuString.target.title)),
    },
  ];

  // Keys persisted to M_State / session storage
  const sessionKeys = [
    "rows",
    "columns",
    "selectedColumns",
    "globalFilterValue",
    "metaPair",
    "advFilterString",
    "filteredColumnModal",
    "valueFilteredColumnModal",
    "selectedRows",
    "metaType",
    "showFilters",
    "orderInfo",
    "operation",
    "isTable",
    "rowEdit",
    "valueAdd",
    "editMap",
    "row",
    "totalRows",
    "views",
    "selectedView",
    "pageSize",
    "page",
    "firstRows",
    "companies",
    "expandedRows",
    "language",
  ];

  function getSessionKeys() {
    return [
      rows,
      columns,
      selectedColumns,
      globalFilterValue,
      metaPair,
      advFilterString,
      filteredColumnModal,
      valueFilteredColumnModal,
      selectedRows,
      metaType,
      showFilters,
      orderInfo,
      operation,
      isTable,
      rowEdit,
      valueAdd,
      editMap,
      row,
      totalRows,
      views,
      selectedView,
      pageSize,
      page,
      firstRows,
      companies,
      expandedRows,
      i18n.language,
    ];
  }

  // Tabs rail width affects min table width (to avoid content clipping)
  const tabsExtraStr =
    typeof tabsRailWidth === "number"
      ? `${tabsRailWidth || 0}px`
      : tabsRailWidth || "0px";

  const tableMinWidth = React.useMemo(() => {
    if (!tabsRailWidth || tabsExtraStr === "0px") return "100%";
    return `calc(100% + ${tabsExtraStr})`;
  }, [tabsRailWidth, tabsExtraStr]);

  // Initial data load
  useEffect(() => {
    if (!isFirstLoop) return;

    setIsLoaded(true);
    getSessionStorageData(id);
    getPermissions();
    setIsFirstLoop(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isFirstLoop]);

  // Rebuild table when language changes
  useEffect(() => {
    if (i18n.language === initialLanguage.current) return;

    setMetaType([]);
    setColumns([]);
    setSelectedColumns([]);
    setFilteredColumnModal([]);
    setAllColumns([]);
    setViews(new Map());
    setSelectedView("Default");
    options();
    setRefresh?.((r) => !r);
    // eslint-disable-next-line
  }, [i18n.language]);

  // Mapping from session keys to setters
  const dataMappings = {
    rows: setRows,
    columns: setColumns,
    selectedColumns: setSelectedColumns,
    globalFilterValue: setGlobalFilterValue,
    metaPair: setMetaPair,
    advFilterString: setAdvFilterString,
    filteredColumnModal: setFilteredColumnModal,
    valueFilteredColumnModal: setValueFilteredColumnModal,
    metaType: setMetaType,
    showFilters: setShowFilters,
    orderInfo: setOrderInfo,
    operation: setOperation,
    isTable: setIsTable,
    rowEdit: setRowEdit,
    valueAdd: (value) => setValueAdd(new Map(value)),
    editMap: (value) => setEditMap(new Map(value)),
    row: setRow,
    totalRows: setTotalRows,
    views: (value) => setViews(new Map(value)),
    selectedView: setSelectedView,
    pageSize: setPageSize,
    page: setPage,
    firstRows: setFirstRows,
    companies: setCompanies,
    expandedRows: setExpandedRows,
  };

  // Load session data from M_State / session storage
  const getSessionStorageData = (sessionID) => {
    setIsLoaded(true);

    if (!isPage) {
      options();
      setViews(new Map(views.set("Default", "?page=1&pagesize=20")));
      return;
    }

    const M_StateData = M_State.find((obj) => obj.key === sessionID)?.value;
    const extractValue = (key, defaultValue = null) =>
      M_StateData?.find((obj) => obj.key === key)?.value ?? defaultValue;

    if (!M_StateData) {
      options();
      return;
    }

    Object.entries(dataMappings).forEach(([key, setter]) => {
      const value = extractValue(key);
      if (value !== undefined) setter(value);
    });

    const tempColumns = extractValue("columns");

    if (
      tempColumns &&
      sessionKeys.some((key) => extractValue(key) !== undefined)
    ) {
      setIsLoaded(false);

      if (stopSaveRows) {
        getWithFilters(
          extractValue("orderInfo"),
          extractValue("page"),
          extractValue("pageSize"),
          extractValue("metaPair")
        );
      }
      return;
    }
    options();
    setViews(new Map(views.set("Default", "?page=1&pagesize=20")));
  };

  // Manual refresh prop
  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      changeView(selectedView);
      setLoading(true);
    }
  }, [refresh, selectedView, setRefresh]);

  // Notify parent when labels should be hidden (based on width)
  useEffect(() => {
    if (onHideLabels) onHideLabels(maxWidth);
  }, [onHideLabels, maxWidth]);

  // Handle changes in tab id or manualId
  useEffect(() => {
    if (manualId === undefined) {
      const newId = M_State.filter((obj) => obj.key === "selectedTab")[0]
        ?.value;
      if (newId !== id) {
        setId(newId);
        getSessionStorageData(newId);
      }
      return;
    }
    if (manualId !== id) {
      setId(manualId);
      getSessionStorageData(manualId);
    }
  }, [manualId, id, M_State]);

  // Persist table state into M_State / session storage
  useEffect(() => {
    if (isPage) {
      const tempArray = [];
      sessionKeys.forEach((key, index) => {
        const value = sessionStorageMachine(
          id,
          key,
          getSessionKeys()[index],
          stopSaveRows
        );
        if (value) tempArray.push(value);
      });
      dispatch(M_StateActions.addM_State({ key: id, value: tempArray }));
    }
    // eslint-disable-next-line
  }, [
    id,
    rows,
    columns,
    selectedColumns,
    globalFilterValue,
    metaPair,
    advFilterString,
    filteredColumnModal,
    valueFilteredColumnModal,
    selectedRows,
    metaType,
    showFilters,
    orderInfo,
    operation,
    isTable,
    rowEdit,
    valueAdd,
    editMap,
    row,
    totalRows,
    views,
    selectedView,
    pageSize,
    page,
    firstRows,
    companies,
    expandedRows,
  ]);

  // Load table metadata (columns, metaType, views, etc.)
  const options = () => {
    setMetaType([]);
    setColumns([]);
    setSelectedColumns([]);
    setFilteredColumnModal([]);

    ctx.setIsLoad(true);

    bufferService.options().then((data) => {
      if (data?.name === "AxiosError") {
        ctx.setIsLoad(false);
        ctx.toastMessages("error", data.message, "");
        return;
      }

      const tempArray = [];
      const tempColumnArray = [];
      const tempSelectedColumnsArray = [];
      const tempFilteredColumnArray = [];
      const tempMetaType = [];

      let tempCompanies = M_State.filter((obj) => obj.key === "companies")[0]
        ?.value;
      if (!tempCompanies?.[0]?.AccountId) {
        tempCompanies = [];
      }
      setCompanies(tempCompanies);

      const orderedData = (data || []).slice().sort((a, b) => {
        const aOrder = typeof a.Order === "number" ? a.Order : 0;
        const bOrder = typeof b.Order === "number" ? b.Order : 0;
        return aOrder - bOrder;
      });

      orderedData.forEach((element) => {
        const isFKText = element.IsForeignKey && element.GroupByInfo;

        tempMetaType.push({
          metaName: element.Name,
          metaType: isFKText ? "string" : element.Type,
          metaDisplay: element.NormalizedName,
          metaIsList: element.IsHiddenList,
          metaIsEdit: element.IsHiddenEdit,
          metaMinLen: element.MinLen,
          metaMaxLen: element.MaxLen,
          metaIsRequired: element.IsRequired,
          metaIsForeignKey: element.IsForeignKey,
          metaForeignKeyName: element.ForeignKeyName,
          metaForeignTableName: element.ForeignTableName,
          metaForeignTableToPassId: element.ForeignTableToPassId,
          metaGroupByInfo: element.GroupByInfo,
        });

        if (element.IsHiddenList) return;

        tempArray.push({ ...element, name: element.Name });
        tempColumnArray.push({ ...element, name: element.Name });
        tempSelectedColumnsArray.push({ ...element, name: element.Name });
        tempFilteredColumnArray.push({ ...element, name: element.Name });
      });

      setMetaType(tempMetaType);
      setFilteredColumnModal(tempFilteredColumnArray);
      setSelectedColumns(tempSelectedColumnsArray);
      setColumns(tempColumnArray);
      setAllColumns(tempArray);

      changeView(selectedView);
    });
  };

  // Basic GET (without saved view)
  const get = () => {
    setRows([]);

    if (!isLoaded) setLoading(true);
    ctx.setIsLoad(true);

    const newUrl = filterService.queryBuilder(
      page,
      pageSize,
      undefined,
      undefined,
      FILTER
    );
    bufferService.filter(newUrl).then((data) => {
      ctx.setIsLoad(false);
      setIsLoaded(false);
      setLoading(false);

      if (typeof onDataLoaded === "function") {
        onDataLoaded();
      }

      if (data.name === "AxiosError") {
        ctx.toastMessages("error", data.message, "");
        setRows([]);
        setTotalRows([]);
        return;
      }
      selectedIncomingValue(
        data,
        value,
        valueIdField,
        selectedRows,
        setSelectedRows
      );
      setTotalRows(data.TotalItems);
      setRows(data.Items);
      if (groupField) {
        setExpandedRows(data.Items);
      }
    });
  };

  // Resolve permissions from rolePermissions string array
  const getPermissions = () => {
    roles.forEach((role) => {
      if (role.match(nameForRole)) {
        const tempPerm = role.split(nameForRole)[1];
        setPermissions((prev) => [...prev, tempPerm]);
      }
    });
  };

  // Change saved view and reload data accordingly
  const changeView = (viewName) => {
    if (!isLoaded) setLoading(true);
    ctx.setIsLoad(true);

    setAdvFilterString("");
    setMetaPair([]);
    setOrderInfo({ columnName: "", orderbool: false });
    setSelectedView(viewName);

    const url = views.get(viewName);
    let objectFilterArray = [];
    let objectOrderBy = { columnName: "", orderbool: false };

    if (url === undefined) {
      get();
      return;
    }

    url.split("&").forEach((elem) => {
      if (elem.includes("filter")) {
        setAdvFilterString("&" + elem);
        let stringFilter = elem.split("filter=")[1];
        stringFilter = stringFilter.split(" and ");
        stringFilter.forEach((el) => {
          const tempArray = el.split(" ");
          let tempObj;
          let tempOp = "";
          if (tempArray[0] === "") tempArray.shift();

          tempObj = getColumnName(tempArray, metaType);
          if (tempArray[1] === "eq") {
            if (tempArray[1] === "eq" && tempArray[2].includes("$")) {
              tempOp = "Contains";
            } else {
              tempOp = "Equals";
            }
          }
          if (tempArray[1] === "Greater") tempOp = "Greater";
          if (tempArray[1] === "Lesser") tempOp = "Lesser";

          let objectFilter;
          if (tempObj.length) {
            objectFilter = {
              name: tempArray[0],
              display: tempObj[0].metaDisplay,
              type: tempObj[0].metaType,
              operation: tempOp,
              value: tempArray[2].replaceAll("'", "").replaceAll("$", ""),
            };
          } else if (tempArray[0].includes("contains")) {
            tempOp = "Contains";
            objectFilter = {
              name: tempArray[0].split(",")[0].replace("contains(", ""),
              display: deRetardationString(
                tempArray[0].split(",")[0].replace("contains(", "")
              ),
              type: "string",
              operation: tempOp,
              value: decodeURIComponent(tempArray[0])
                .split(",")[1]
                .replace(")", "")
                .replaceAll("'", ""),
            };
          } else {
            objectFilter = {
              name: tempArray[0],
              display: deRetardationString(tempArray[0]),
              type: getType(
                tempArray[2].replaceAll("'", "").replaceAll("$", "")
              ),
              operation: tempOp,
              value: getValue(
                tempArray[2].replaceAll("'", "").replaceAll("$", "")
              ),
            };
          }

          objectFilterArray.push(objectFilter);
        });
      }

      if (elem.includes("order")) {
        let stringOrder = elem.split("orderby=")[1];
        stringOrder = stringOrder.split(" ");
        let tempBool = "";
        if (stringOrder[1] === "asc") tempBool = false;
        if (stringOrder[1] === "desc") tempBool = true;
        objectOrderBy = {
          columnName: stringOrder[0].toUpperCase(),
          orderbool: tempBool,
        };
      }
    });

    setMetaPair(objectFilterArray);
    setOrderInfo(objectOrderBy);

    const newUrl = filterService.queryBuilder(
      page,
      pageSize,
      objectOrderBy,
      objectFilterArray,
      FILTER
    );

    bufferService.filter(newUrl).then((res) => {
      ctx.setIsLoad(false);
      setLoading(false);
      setIsLoaded(false);

      if (typeof onDataLoaded === "function") {
        onDataLoaded();
      }

      if (res.name === "AxiosError") {
        ctx.toastMessages("error", res.message, "");
        setRows([]);
        setTotalRows([]);
        return;
      }

      selectedIncomingValue(
        res,
        value,
        valueIdField,
        selectedRows,
        setSelectedRows
      );
      setTotalRows(res.TotalItems);
      setRows(res.Items);
      if (groupField) {
        setExpandedRows(res.Items);
      }
    });
  };

  // GET with filters, sorting, pagination
  const getWithFilters = (ord, newPage, newRows, adv) => {
    if (!isLoaded) setLoading(true);
    ctx.setIsLoad(true);

    const customPage = newPage || page;
    const customPageSize = newRows || pageSize;
    const customOrder = ord || orderInfo;
    const customAdv = adv || metaPair;

    const url = filterService.queryBuilder(
      customPage,
      customPageSize,
      customOrder,
      customAdv,
      FILTER
    );

    bufferService.filter(url).then((res) => {
      ctx.setIsLoad(false);
      setLoading(false);
      setIsLoaded(false);

      if (typeof onDataLoaded === "function") {
        onDataLoaded();
      }

      if (res.name === "AxiosError") {
        ctx.toastMessages("error", res.message, "");
        setRows([]);
        setTotalRows([]);
        return;
      }

      selectedIncomingValue(
        res,
        value,
        valueIdField,
        selectedRows,
        setSelectedRows
      );
      setTotalRows(res.TotalItems);
      setRows(res.Items);
      if (groupField) {
        setExpandedRows(res.Items);
      }
    });
  };

  // Handle sort when clicking column header
  const order = (e) => {
    if (e.target.id === "columnTable") {
      setLoading(true);
      const column = e.target.title;
      let newOrderInfo = {};
      if (orderInfo.columnName === column) {
        if (orderInfo.orderbool === false) {
          const resetOrder = { columnName: "", orderbool: false };
          setOrderInfo(resetOrder);
          if (globalFilterValue !== "") {
            globalSearch(globalFilterValue, resetOrder);
          } else {
            getWithFilters(resetOrder);
          }
          return;
        }
        newOrderInfo = { columnName: column, orderbool: !orderInfo.orderbool };
      } else {
        newOrderInfo = { columnName: column, orderbool: true };
      }
      setOrderInfo(newOrderInfo);

      if (globalFilterValue === "") {
        const newUrl = filterService.queryBuilder(
          page,
          pageSize,
          newOrderInfo,
          metaPair,
          FILTER
        );
        ctx.setIsLoad(true);
        bufferService.filter(newUrl).then((res) => {
          ctx.setIsLoad(false);
          setLoading(false);
          setIsLoaded(false);

          if (typeof onDataLoaded === "function") {
            onDataLoaded();
          }

          if (res.name === "AxiosError") {
            ctx.toastMessages("error", res.message, "");
            setRows([]);
            setTotalRows([]);
            return;
          }

          selectedIncomingValue(
            res,
            value,
            valueIdField,
            selectedRows,
            setSelectedRows
          );
          setTotalRows(res.TotalItems);
          setRows(res.Items);
          if (groupField) {
            setExpandedRows(res.Items);
          }
        });
      } else {
        globalSearch(globalFilterValue, newOrderInfo);
      }
    }
  };

  // Toggle column visibility
  const onColumnToggle = (data) => {
    let tempArray = [];
    if (selectedColumns.find((element) => element.Name === data.name)) {
      tempArray = selectedColumns.filter((el) => el.Name !== data.name);
      setSelectedColumns(tempArray);
    } else {
      setSelectedColumns((prev) => [...prev, data]);
    }
  };

  const handleOpenDelete = (line) => {
    if (Array.isArray(line) && line.length > 0) {
      setDataContextMenu(null);
      setOpenConfirmDelete(true);
    } else if (
      line &&
      typeof line === "object" &&
      Object.keys(line).length > 0
    ) {
      setDataContextMenu(line);
      setOpenConfirmDelete(true);
    } else {
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  const handleCloseConfirmDelete = () => {
    setDataContextMenu(null);
    setOpenConfirmDelete(false);
  };

  const handleOpenCopy = (selected) => {
    const row = selected || null;

    if (row) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("Copy", row);
        return;
      }
      setRow(row);
      setOperation("Copy");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  function handleView(selected) {
    const row = selected || null;
    if (row) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("View", row);
        return;
      }

      setRow(row);
      setOperation("View");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  }

  const handleOpenDialogAdd = () => {
    if (!crudInPopUp(metaType)) {
      sendInfoCrud("Create");
      return;
    }
    setOperation("Create");
    setRow(null);
    setIsTable(false);
  };

  const handleOpenEdit = (selected) => {
    const row = selected || null;
    if (row) {
      if (!crudInPopUp(metaType)) {
        sendInfoCrud("Edit", row);
        return;
      }
      setRow(row);
      setOperation("Edit");
      setIsTable(false);
    } else {
      setIsTable(true);
      ctx.toastMessages("error", "", t("SelectedLine"));
    }
  };

  // DataTable selection logic, supports single/multiple and "select all"
  const onSelectionChange = (e) => {
    let tempValue = [];

    if (isSelectSingle) {
      if (e.value) tempValue = [e.value];
      else tempValue = [];
    } else if (e.type === "all") {
      if (e.value.length) {
        selectedRows.forEach((obj) => {
          if (!e.value.find((rowObj) => rowObj.Id === obj.Id)) {
            tempValue.push(obj);
          }
        });
        tempValue.push(...e.value);
      } else {
        selectedRows.forEach((obj) => {
          if (!rows.find((rowObj) => rowObj.Id === obj.Id)) {
            tempValue.push(obj);
          }
        });
      }
    } else {
      tempValue = [...e.value];
    }

    if (isParentTable && setParentLine) {
      setParentLine(tempValue);
    }

    if (onPassLine) {
      onPassLine(tempValue);
    }

    setSelectedRows([...tempValue]);
  };

  // Delete logic (single row or multiple selected rows)
  const postDelete = () => {
    let tempArray = [];
    if (dataContextMenu) {
      bufferService.delete(dataContextMenu.Id).then((data) => {
        if (data.name === "AxiosError") {
          ctx.toastMessages(
            "error",
            data.response.data.errors,
            t("LabelTryAgain")
          );
        } else {
          setDataContextMenu(null);
          ctx.toastMessages("success", "", t("RemoveLine"));
          setOpenConfirmDelete(false);
          getWithFilters();
        }
      });
    } else if (selectedRows.length) {
      tempArray = selectedRows;
    }

    tempArray.forEach((elem) => {
      bufferService.delete(elem.Id).then((data) => {
        if (data.name === "AxiosError") {
          ctx.toastMessages(
            "error",
            data.response.data.errors,
            t("LabelTryAgain")
          );
        } else {
          setSelectedRows(null);
          ctx.toastMessages("success", "", t("RemoveLine"));
          setOpenConfirmDelete(false);
          getWithFilters();
        }
      });
    });
  };

  // Paginator onPage handler: updates page, pageSize and reloads data
  const onPage = (event) => {
    setFirstRows(event.first);
    setExpandedRows([]);
    const newPage = event.page + 1;
    setPage(newPage);
    setPageSize(event.rows);
    getWithFilters("", newPage, event.rows);
  };

  // Right-click on header to show column context menu
  const rightClick = (e) => {
    if (e.target.id !== "columnTable") return;
    setColumnContextMenuString(e);
    ccm.current.show(e);
  };

  // Global search across all string/int columns
  const globalSearch = (valueSearch, order) => {
    if (!isLoaded) setLoading(true);
    ctx.setIsLoad(true);

    const tempNewValue = valueSearch;
    let tempString = "";
    let first = true;
    const tempOrder = order || orderInfo;
    let tempFullQuery = "";
    setGlobalFilterValue(tempNewValue);

    if (tempNewValue !== "") {
      metaType.forEach((tempCol) => {
        switch (tempCol.metaType) {
          case "string":
            tempString = tempString.concat(
              first
                ? ` contains(${tempCol.metaName}, '${encodeURIComponent(
                    tempNewValue
                  )}')`
                : ` or contains(${tempCol.metaName}, '${encodeURIComponent(
                    tempNewValue
                  )}')`
            );
            first = false;
            break;
          case "int32" || "int":
            if (Number(tempNewValue) || Number(tempNewValue) === 0) {
              tempString = tempString.concat(
                first
                  ? ` ${tempCol.metaName} eq ${encodeURIComponent(
                      tempNewValue
                    )}`
                  : ` or ${tempCol.metaName} eq ${encodeURIComponent(
                      tempNewValue
                    )}`
              );
              first = false;
            }
            break;
          default:
            break;
        }
      });

      tempFullQuery = FILTER
        ? filterService.queryBuilder(
            page,
            pageSize,
            tempOrder,
            metaPair,
            FILTER + " and (" + tempString + ")"
          )
        : filterService.queryBuilder(
            page,
            pageSize,
            tempOrder,
            metaPair,
            tempString
          );
    } else {
      tempFullQuery = filterService.queryBuilder(
        page,
        pageSize,
        tempOrder,
        metaPair,
        FILTER
      );
    }

    bufferService.filter(tempFullQuery).then((res) => {
      ctx.setIsLoad(false);
      setLoading(false);
      setIsLoaded(false);

      if (typeof onDataLoaded === "function") {
        onDataLoaded();
      }

      if (res.name === "AxiosError") {
        ctx.toastMessages("error", res.message, "");
        setRows([]);
        setTotalRows([]);
        return;
      }
      selectedIncomingValue(
        res,
        value,
        valueIdField,
        selectedRows,
        setSelectedRows
      );
      setTotalRows(res.TotalItems);
      setRows(res.Items);
      if (groupField) {
        setExpandedRows(res.Items);
      }
    });
  };

  // Load audit history for selected row
  const getAudit = (line) => {
    if (!line) {
      ctx.toastMessages("error", "", t("SelectedLine"));
      return;
    }
    auditService.getId(line?.Id).then((resAudit) => {
      if (Array.isArray(resAudit)) {
        setAuditData(resAudit);
        setOpenAudit(true);
      } else {
        ctx.toastMessages("error", "", t("noAudit"));
      }
    });
  };

  const closeAudit = () => {
    setOpenAudit(false);
    setAuditData([]);
  };

  // Checkbox column renderer (boolean)
  const verifiedBodyTemplateLocal = (rowData, fieldName) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <MCheckBox disabled checked={rowData[fieldName]} noPadding />
      </div>
    );
  };

  // Status label renderer with colored background
  const statusBodyTemplateLocal = (rowData, fieldName) => {
    const colorBody = getSeverity(rowData[fieldName], fieldName);
    if (hideDefaultColor && colorBody === "#000000") {
      return (
        <div
          style={{
            display: "flex",
            fontWeight: "bolder",
            justifyContent: "center",
            textAlign: "center",
            alignItems: "center",
            borderRadius: "6px",
            padding: "3px 10px",
            fontSize: "0.8rem",
          }}
        >
          {t(rowData[fieldName])}
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          fontWeight: "bolder",
          color: "white",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          borderRadius: "6px",
          padding: "3px 10px",
          fontSize: "0.8rem",
          backgroundColor: colorBody,
        }}
      >
        {t(rowData[fieldName])}
      </div>
    );
  };

  // Image column renderer (byte[])
  const imageBodyTemplateLocal = (rowData, fieldName) => {
    return (
      <div
        style={{
          width: "50px",
          display: "flex",
          fontWeight: "bolder",
          color: "white",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          borderRadius: "6px",
          padding: "2px 10px",
          fontSize: "0.8rem",
        }}
      >
        <img
          width={"25px"}
          src={
            rowData[fieldName]
              ? `data:image/jpeg;base64,${rowData[fieldName]}`
              : "./images/noImage.png"
          }
          alt={fieldName}
        />
      </div>
    );
  };

  // datetime column renderer (simple T -> space)
  const dateTimeBodyTemplateLocal = (rowData, fieldName) => {
    const raw = rowData[fieldName];
    if (!raw) return null;

    const text = raw.toString().replace("T", " ").split(".")[0];

    return <span>{text}</span>;
  };

  // Header cell renderer (sorting, info icon, tooltip)
  const headerElement = (col, iconForm) => {
    if (
      col.Type !== "boolean" &&
      col.Type !== "string" &&
      col.Type !== "int32" &&
      col.Type !== "datetime" &&
      col.Type !== "int" &&
      col.Type !== "byte[]" &&
      col.Type !== "bool"
    ) {
      if (!removeHeaders.includes(col.Header)) {
        return (
          <div
            id="columnTable"
            title={col.Header}
            style={{ display: "flex", cursor: "pointer" }}
          >
            <div
              id="columnTable"
              title={col.Header}
              style={{ marginRight: "10px" }}
            >
              {col.NormalizedName}
            </div>
            {infoElement?.get(col.Header) && (
              <div className="tooltip">
                <span className="tooltiptext">
                  <ul style={{ listStyle: "none" }}>
                    {infoElement.get(col.Header).map((info, index) => (
                      <li
                        key={info + index || index}
                        style={{ marginBottom: "10px" }}
                      >
                        {info}
                      </li>
                    ))}
                  </ul>
                </span>
              </div>
            )}
          </div>
        );
      }
    }

    if (!removeHeaders.includes(col.Header)) {
      return (
        <MButton
          bold
          noHover
          marginNone
          iconPosition="right"
          tooltip={col.Header}
          labelColor={secondColor}
          onClick={(e) => order(e)}
          label={col.NormalizedName}
          icon={
            <>
              {columnOrderHeaderIcon(iconForm, col)}
              {columnInfoHeaderIcon(infoElement, col)}
            </>
          }
        />
      );
    }
  };

  // Build dynamic DataTable columns based on column metadata
  const dynamicColumns = selectedColumns.map((col) => {
    let iconForm = "default";
    if (orderInfo.columnName === col.Header) {
      if (orderInfo.orderbool === false) {
        iconForm = "asc";
      }
      if (orderInfo.orderbool === true) {
        iconForm = "desc";
      }
    }
    if (!removeColumns?.includes(col.Name)) {
      if (col.Type === "boolean" || col.Type === "bool") {
        return (
          <Column
            id="head"
            key={col?.Name}
            dataType={col?.Type}
            field={col?.Name}
            header={headerElement(col, iconForm)}
            body={(data) => verifiedBodyTemplateLocal(data, col.Name)}
            headerStyle={{
              borderBottom: `2px solid ${color}`,
              color: secondColor,
            }}
          />
        );
      }
      if (colorOptions?.get(col.Name) || col.Name === "Status") {
        return (
          <Column
            id="head"
            key={col?.Name}
            dataType={col.Type}
            field={col.Name}
            header={headerElement(col, iconForm)}
            showAddButton={true}
            body={(data) =>
              statusBodyTemplateLocal(
                data,
                col.Name,
                colorOptions,
                hideDefaultColor,
                t
              )
            }
            headerStyle={{
              borderBottom: `2px solid ${color}`,
              color: secondColor,
            }}
          />
        );
      }

      if (col.Type === "byte[]") {
        return (
          <Column
            id="head"
            key={col?.Name}
            dataType={col.Type}
            field={col.Name}
            header={headerElement(col, iconForm)}
            showAddButton={true}
            body={(data) => imageBodyTemplateLocal(data, col.Name)}
            headerStyle={{
              borderBottom: `2px solid ${color}`,
              color: secondColor,
            }}
          />
        );
      }

      if (col.Type === "datetime") {
        return (
          <Column
            id="head"
            key={col?.Name}
            dataType={col.Type}
            field={col.Name}
            header={headerElement(col, iconForm)}
            showAddButton={true}
            body={(data) => dateTimeBodyTemplateLocal(data, col.Name)}
            headerStyle={{
              borderBottom: `2px solid ${color}`,
              color: secondColor,
            }}
          />
        );
      }

      return (
        <Column
          id="head"
          key={col?.Name}
          dataType={col.Type}
          field={col.Name}
          header={headerElement(col, iconForm)}
          showAddButton={true}
          headerStyle={{
            borderBottom: `2px solid ${color}`,
            color: secondColor,
          }}
        />
      );
    }
    return null;
  });

  function contextDeleteButton() {
    const tempObj = { ...selectContext };
    setDataContextMenu(tempObj);
    handleOpenDelete(selectContext);
  }

  // Build context menu items dynamically based on permissions and config
  const rebuildContextMenu = () => {
    const tempArray = [];
    if (!removeItem.includes("View")) {
      tempArray.push({
        label: t("view"),
        icon: (
          <i
            className="pi pi-search"
            style={{ color: color, paddingRight: "5px" }}
          ></i>
        ),
        command: () => rebuildContextCommand("View"),
      });
    }
    if (permissions.includes("Update") && !removeItem.includes("Update")) {
      tempArray.push({
        label: t("Edit"),
        icon: "pi pi-pencil",
        command: () => rebuildContextCommand("Edit"),
      });
    }
    if (
      permissions.includes("Create") &&
      !removeItem.includes("Create") &&
      !removeItem.includes("Copy")
    ) {
      tempArray.push({
        label: t("Copy"),
        icon: "pi pi-copy",
        command: () => rebuildContextCommand("Copy"),
      });
    }
    if (permissions.includes("Delete") && !removeItem.includes("Delete")) {
      tempArray.push({
        label: t("Delete"),
        icon: "pi pi-trash",
        command: () => rebuildContextCommand("Delete"),
      });
    }
    if (permissions.includes("Audit") && !removeItem.includes("Audit")) {
      tempArray.push({
        label: t("Audit"),
        icon: "pi pi-search",
        command: () => getAudit(selectContext),
      });
    }
    if (contextMenu.length) {
      contextMenu.forEach((objContext) => {
        tempArray.push({
          label: objContext.label,
          icon: objContext.icon,
          command: () => objContext.command(selectContext),
        });
      });
    }

    return tempArray;
  };

  function rebuildContextCommand(operationCmd) {
    if (replaceFunctions?.get(operationCmd)) {
      return replaceFunctions?.get(operationCmd)(selectContext);
    } else {
      switch (operationCmd) {
        case "View":
          return handleView(selectContext);
        case "Edit":
          return handleOpenEdit(selectContext);
        case "Copy":
          return handleOpenCopy(selectContext);
        case "Delete":
          return contextDeleteButton();
        default:
          break;
      }
    }
  }

  // Open CRUD page in new tab
  function sendInfoCrud(tempOperation, line) {
    ctx.setPassValue({
      metaType: metaType,
      entity: entity,
      operation: tempOperation,
      row: line ?? null,
      isChildTable: isChildTable ?? null,
      parentLine: parentLine ?? null,
      field: field ?? null,
      companies: companies ?? null,
    });
    ctx2.newTab("createPage", tempOperation + " " + entity);
  }

  // Paginator component: now includes rows-per-page dropdown in both layouts
  function paginatorElement() {
    const commonProps = {
      className: "paginator",
      rows: pageSize,
      totalRecords: totalRows,
      first: firstRows,
      onPageChange: (e) => onPage(e),
      rowsPerPageOptions: [10, 20, 50, 100],
    };

    if (adjustingTableMedia3) {
      // Wider screens: full paginator with first/last, page links, and page size dropdown
      return (
        <Paginator
          {...commonProps}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        />
      );
    }

    // Small screens: compact layout but still with page size dropdown
    return (
      <Paginator
        {...commonProps}
        template={{
          layout:
            "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown",
        }}
      />
    );
  }

  // Render the main DataTable with paginator and header
  function returnTable() {
    return (
      <div
        className="secondDivDataTable"
        style={{ overflowX: "auto" }}
        onContextMenu={(e) => {
          rightClick(e);
        }}
        role="menu"
        tabIndex={0}
      >
        <DataTable
          emptyMessage={t("LabelNoresultsFound")}
          className="defaultTable"
          tableStyle={{ minWidth: tableMinWidth }}
          scrollable
          header={
            <TableHeader
              views={views}
              selectedView={selectedView}
              showFilters={showFilters}
              metaType={metaType}
              setMetaPair={setMetaPair}
              metaPair={metaPair}
              allColumns={allColumns}
              color={color}
              removeColumns={removeColumns}
              removeFilters={removeFilters}
              filtersOnTable={filtersOnTable}
              changeView={changeView}
              onColumnToggle={onColumnToggle}
              selectedColumns={selectedColumns}
              filteredColumnModal={filteredColumnModal}
              valueFilteredColumnModal={valueFilteredColumnModal}
              globalFilterValue={globalFilterValue}
              globalSearch={globalSearch}
              inputFilter={inputFilter}
              value={value}
              selectedRows={selectedRows}
              newButtons={newButtons}
              entity={entity}
              includeTemplateExcel={includeTemplateExcel}
              permissions={permissions}
              includeImportExcel={includeImportExcel}
              removeItem={removeItem}
              isCrud={isCrud}
              setLoading={setLoading}
              replaceFunctions={replaceFunctions}
              setAdvFilterString={setAdvFilterString}
              columns={columns}
              setIsTable={setIsTable}
              setRow={setRow}
              setOperation={setOperation}
              handleOpenDialogAdd={handleOpenDialogAdd}
              handleOpenDelete={handleOpenDelete}
              handleOpenCopy={handleOpenCopy}
              handleOpenEdit={handleOpenEdit}
              setFilteredColumnModal={setFilteredColumnModal}
              setValueFilteredColumnModal={setValueFilteredColumnModal}
              getWithFilters={getWithFilters}
              setSelectedRows={setSelectedRows}
              setSelectedView={setSelectedView}
              get={get}
              setViews={setViews}
              setExpandedRows={setExpandedRows}
              groupField={groupField}
              setIsLoaded={setIsLoaded}
              setRows={setRows}
              setTotalRows={setTotalRows}
              valueIdField={valueIdField}
              bufferService={bufferService}
              FILTER={FILTER}
              orderInfo={orderInfo}
              pageSize={pageSize}
              page={page}
            />
          }
          value={rows}
          size="small"
          selection={!noSelect && selectedRows}
          selectionMode={!noSelect && "multiple"}
          stripedRows
          responsiveLayout="scroll"
          breakpoint="960px"
          scrollHeight={height}
          reorderableColumns
          sortOrder={sortFields("sortOrder", orderFields, groupField)}
          sortField={sortFields("sortField", orderFields, groupField)}
          groupRowsBy={groupField}
          rowGroupMode="subheader"
          expandableRowGroups={rowGroup || false}
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            if (e.data && expandedRows) {
              let newValue = e?.data.filter((x) => !expandedRows.includes(x));
              let oldValue = expandedRows.filter((x) => !e?.data.includes(x));

              if (oldValue.length > newValue.length) {
                setExpandedRows(
                  expandedRows.filter(
                    (prev) => prev[groupField] !== oldValue[0][groupField]
                  )
                );
              }
              if (newValue.length > oldValue.length) {
                setExpandedRows(e.data);
              }
            } else {
              setExpandedRows(e.data);
            }
          }}
          rowGroupHeaderTemplate={headerTemplate}
          rowGroupFooterTemplate={footerTemplate}
          ref={dt}
          onSelectionChange={onSelectionChange}
          contextMenuSelection={selectContext}
          onContextMenuSelectionChange={(e) => {
            setSelectContext(e.value);
            if (onPassContext) onPassContext(e.value);
          }}
          onContextMenu={(e) =>
            rebuildContextMenu().length !== 0 &&
            cm.current.show(e.originalEvent)
          }
        >
          {isSelectSingle ? (
            <Column
              headerStyle={{
                borderBottom: `2px solid ${color}`,
                color: secondColor,
              }}
              selectionMode="single"
              className="OpenTableCheckbox"
              frozen
            />
          ) : (
            !noSelect && (
              <Column
                headerStyle={{
                  borderBottom: `2px solid ${color}`,
                  color: secondColor,
                }}
                selectionMode="multiple"
                className="TableSelectionColumn"
                frozen
              />
            )
          )}
          {dynamicColumns}
        </DataTable>
        {paginatorElement()}
      </div>
    );
  }

  // CRUD modal content
  function returnCrudMenu() {
    return (
      <CrudTable
        metaType={metaType}
        entity={entity}
        onGetWithFilters={getWithFilters}
        operation={operation}
        setIsTable={setIsTable}
        rowEdit={rowEdit}
        setRowEdit={setRowEdit}
        valueAdd={valueAdd}
        setValueAdd={setValueAdd}
        editMap={editMap}
        setEditMap={setEditMap}
        row={row}
        isChildTable={isChildTable}
        parentLine={parentLine}
        field={field}
        companies={companies}
        isCrudInPopUp={crudInPopUp(metaType)}
      />
    );
  }

  // Reload when FILTER changes
  useEffect(() => {
    if (isLoaded) return;
    changeView(selectedView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FILTER]);

  // Auto-select first row in single selection mode when requested
  useEffect(() => {
    if (!autoSelectFirstRow) return;
    if (noSelect) return;
    if (!isSelectSingle) return;
    if (!rows || rows.length === 0) return;
    if (selectedRows && selectedRows.length > 0) return;

    const firstRow = rows[0];
    const newSelection = [firstRow];

    setSelectedRows(newSelection);

    if (isParentTable && setParentLine) {
      setParentLine(newSelection);
    }

    if (onPassLine) {
      onPassLine(newSelection);
    }
  }, [
    autoSelectFirstRow,
    noSelect,
    isSelectSingle,
    rows,
    selectedRows,
    isParentTable,
    setParentLine,
    onPassLine,
  ]);

  return (
    <>
      {isLoaded ? (
        <div style={loadingStylesFunc(loading, adjustingTableMedia2)}>
          <LoadingBar />
        </div>
      ) : (
        <div>
          {disable && (
            <div className="disable" style={{ ...loadingStyle }}></div>
          )}
          <ContextMenu
            model={columnContextMenu}
            onShow={(e) => cm.current.hide(e.originalEvent)}
            ref={ccm}
          />
          {rebuildContextMenu().length !== 0 && (
            <ContextMenu
              model={rebuildContextMenu()}
              onShow={(e) => ccm.current.hide(e.originalEvent)}
              onHide={(e) => {
                if (e && e.target.className !== "p-menuitem-link") {
                  if (onPassContext) onPassContext(null);
                }
                setSelectContext(null);
              }}
              ref={cm}
            />
          )}
          {crudInPopUp(metaType) && (
            <>
              {returnTable()}
              <MMessageBoard
                open={!isTable}
                onClose={() => setIsTable(false)}
                title={operation + " " + entity}
                minWidth={(match2 && "400px") || (match && "850px") || "900px"}
                body={returnCrudMenu()}
              />
            </>
          )}
          {!crudInPopUp(metaType) &&
            (isTable ? returnTable() : returnCrudMenu())}
          <TableMessageBoards
            openConfirmDelete={openConfirmDelete}
            handleCloseConfirmDelete={handleCloseConfirmDelete}
            entityName={entityName}
            postDelete={postDelete}
            openAudit={openAudit}
            closeAudit={closeAudit}
            sortAuditData={sortAuditData}
            auditActionColor={auditActionColor}
            auditData={auditData}
          />
        </div>
      )}
      <div style={loadingStylesFunc(loading, adjustingTableMedia2)}>
        <LoadingBar />
      </div>
    </>
  );
}

MTable.propTypes = {
  entityName: PropTypes.string,
  useSessionStorage: PropTypes.bool,
  crud: PropTypes.bool,
  onPassLine: PropTypes.func,
  onPassContext: PropTypes.func,
  tableHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  manualId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rolePermissions: PropTypes.array,
  lineContext: PropTypes.array,
  extraButtons: PropTypes.array,
  removeContextItems: PropTypes.array,
  onHideLabels: PropTypes.func,
  toUseFilter: PropTypes.object,
  toUseFilterName: PropTypes.string,
  selectSingle: PropTypes.bool,
  refresh: PropTypes.bool,
  setRefresh: PropTypes.func,
  isParentTable: PropTypes.bool,
  isChildTable: PropTypes.bool,
  setParentLine: PropTypes.func,
  parentLine: PropTypes.array,
  field: PropTypes.string,
  roleName: PropTypes.string,
  FILTER: PropTypes.string,
  replaceFunctions: PropTypes.object,
  color: PropTypes.string,
  secondColor: PropTypes.string,
  rowGroup: PropTypes.bool,
  groupField: PropTypes.array,
  headerTemplate: PropTypes.element,
  footerTemplate: PropTypes.element,
  colorOptions: PropTypes.object,
  noSelect: PropTypes.bool,
  maxRows: PropTypes.number,
  includeImportExcel: PropTypes.bool,
  includeTemplateExcel: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  valueIdField: PropTypes.string,
  removeHeaders: PropTypes.array,
  filtersOnTable: PropTypes.bool,
  infoElement: PropTypes.object,
  orderFields: PropTypes.string,
  hideDefaultColor: PropTypes.bool,
  inputFilter: PropTypes.bool,
  stopSaveRows: PropTypes.bool,
  removeColumns: PropTypes.array,
  removeFilters: PropTypes.array,
  disable: PropTypes.bool,
  tabsRailWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDataLoaded: PropTypes.func,
  autoSelectFirstRow: PropTypes.bool,
};
